import { CoreLayoutView } from '@core/widgets';
import { useSkipFirstRender } from '@libs/core';
import clsx from 'clsx';
import React from 'react';
import { TFloatingLayoutOption } from '../../types/options';
import { getBackgroundHref, resolveCssColor } from '../Image/imageUtils';
import styles from './css/floatingLayout.module.css';

export type TFloatingLayoutViewProps = {
    /** Value for the `data-test` attribute (widget uid in server-driven scenarios). */
    uid?: string;
    /** Pre-resolved visibility state (derived from useBehavior one level up); drives the fade-in/out animation. */
    isHide?: boolean;
    /** Floating position, selects the animation direction and layout class. */
    alignment?: TFloatingLayoutOption['alignment'];
    /** Server-driven theme style name forwarded to CoreLayoutView for theme resolution. */
    styleName?: string;
    children?: React.ReactNode;
} & Pick<TFloatingLayoutOption, 'margin' | 'backgroundColor' | 'backgroundImage'>;

/**
 * Composable floating layout building block for use inside complex widgets.
 * Owns only rendering logic — the fade-in/out animation classes (via
 * useSkipFirstRender to avoid animating on initial mount) and the wrapper
 * element — and delegates inner theme resolution to CoreLayoutView.
 *
 * Accepts pre-resolved behavior props (isHide) and plain display values from
 * the parent. It NEVER receives a `widget` object — every value is an explicit
 * prop, resolved one level up (see FloatingLayoutWidget).
 */
const FloatingLayoutView = ({
    uid,
    isHide = false,
    alignment = 'top',
    styleName,
    margin,
    backgroundColor,
    backgroundImage,
    children,
}: TFloatingLayoutViewProps) => {
    const firstRender = useSkipFirstRender(isHide);

    const classNames = clsx(
        styles.floatingLayout,
        styles[`aliment-${alignment}`],
        !firstRender && isHide && alignment === 'top' && styles.fadeOutTop,
        !firstRender && isHide && alignment !== 'top' && styles.fadeOut,
        !firstRender && !isHide && alignment === 'top' && styles.fadeInTop,
        !firstRender && !isHide && alignment !== 'top' && styles.fadeIn,
    );

    return (
        <div data-test={uid} className={classNames}>
            <CoreLayoutView
                uid={'floatingLayoutInner_' + uid}
                isHide={isHide}
                styleName={styleName}
                styleProps={{
                    backgroundColor: resolveCssColor(backgroundColor),
                    backgroundImage: getBackgroundHref(backgroundImage),
                    margin,
                }}
            >
                {children}
            </CoreLayoutView>
        </div>
    );
};

export { FloatingLayoutView };
